// @flow
import async from 'async';
import qs from 'querystring';

import type { ClubPreviewType, ClubType, SearchClub } from 'types/Club';
import { SORT_NAME, SORT_DISTANCE } from 'constants/sortConstants';
import { getDistanceFromLatLonInKm } from 'utils/distanceUtils';
import { _fetch } from 'utils/externalApi';

export function queryClubsAndCities(
  clubList: Array<ClubPreviewType>,
  query: string
): Promise<{ cities: Array<string>, clubs: Array<ClubPreviewType> }> {
  return new Promise((resolve, reject) => {
    let cities = [];
    let clubs = [];

    async.each(
      clubList,
      (club, callback) => {
        if (clubs.length < 5 && club.searchName.toLowerCase().includes(query)) clubs = [...clubs, club];
        if (cities.length < 5) {
          const city = club.adress.locality.toLowerCase();
          if (city.includes(query)) cities = [...cities.filter(c => c !== club.adress.locality), club.adress.locality];
        }
        callback();
      },
      err => {
        if (!err) resolve({ cities, clubs });
        else reject(err);
      }
    );
  });
}

function isQueryInClubName(query: string, club: ClubPreviewType) {
  return club.searchName.toLowerCase().includes(query);
}

function isQueryInClubCity(query: string, club: ClubPreviewType) {
  const city = club.adress.locality.toLowerCase();
  return city.includes(query);
}

function clubHasRugbyTypes(club: ClubPreviewType, types: Array<string>) {
  return types.every(type => club.practices.includes(type));
}

function clubHasCompetitionTypes(club: ClubPreviewType, types: Array<string>) {
  return types.every(type => club.competitions.includes(type));
}

function clubIsInDistance(club: ClubPreviewType, lat: number, long: number, distance: string) {
  return distance === '' || getDistanceFromLatLonInKm(club.lat, club.long, lat, long) < parseInt(distance, 10);
}

export function getClubsNearby(
  clubs: Array<ClubPreviewType>,
  currentClub: ClubType,
  distance: string,
  limit: ?number
): Array<ClubPreviewType> {
  if (clubs.length === 0) {
    return [];
  }

  const clubNearby = clubs
    .filter(c => c.lat !== currentClub.lat && c.long !== currentClub.long)
    .filter(club => clubIsInDistance(club, currentClub.lat, currentClub.long, distance) && club)
    .sort((a, b) => {
      const { lat, long } = currentClub;
      if (getDistanceFromLatLonInKm(a.lat, a.long, lat, long) < getDistanceFromLatLonInKm(b.lat, b.long, lat, long))
        return -1;
      if (getDistanceFromLatLonInKm(a.lat, a.long, lat, long) > getDistanceFromLatLonInKm(b.lat, b.long, lat, long))
        return 1;
      return 0;
    })
    .slice(0, limit || clubs.length);

  return clubNearby;
}

export function queryClubsWithFilter(
  clubList: Array<ClubPreviewType>,
  lat: number,
  long: number,
  query: string,
  rugbyTypes: Array<string>,
  competitionTypes: Array<string>,
  distance: string
): Promise<Array<ClubPreviewType>> {
  return new Promise((resolve, reject) => {
    let clubs = [];

    async.each(
      clubList,
      (club, callback) => {
        if (
          (query === '' || isQueryInClubName(query, club) || isQueryInClubCity(query, club)) &&
          clubHasRugbyTypes(club, rugbyTypes) &&
          clubHasCompetitionTypes(club, competitionTypes) &&
          clubIsInDistance(club, lat, long, distance)
        ) {
          clubs = [...clubs, club];
        }
        callback();
      },
      err => {
        if (!err) resolve(clubs);
        else reject(err);
      }
    );
  });
}

export function sortClubs(
  clubs: ClubPreviewType[],
  sort: SORT_NAME | SORT_DISTANCE,
  currentLat: number,
  currentLong: number
): ClubPreviewType[] {
  if (sort === SORT_NAME)
    return clubs.sort((a, b) => {
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    });
  else
    return clubs.sort((a, b) => {
      const distanceA = a._geoDistance
        ? a._geoDistance
        : getDistanceFromLatLonInKm(a.lat, a.long, currentLat, currentLong);
      const distanceB = b._geoDistance
        ? b._geoDistance
        : getDistanceFromLatLonInKm(b.lat, b.long, currentLat, currentLong);
      return distanceA < distanceB ? -1 : distanceA > distanceB ? 1 : 0;
    });
}

export function getDisplayedClubs(clubs: ClubPreviewType[], perPage: number, currentPage: number): ClubPreviewType[] {
  return clubs.slice((currentPage - 1) * perPage, currentPage * perPage);
}

function uniqueArray(arr) {
  var a = [];
  for (var i = 0, l = arr.length; i < l; i++) if (a.indexOf(arr[i]) === -1 && arr[i] !== '') a.push(arr[i]);
  return a;
}
export function getPracticesFromClubs(clubs: Array<ClubPreviewType>): Array<string> {
  if (!Array.from) {
    /**
     * Cas des vieux IE
     */
    return clubs.reduce((practices, club) => {
      return uniqueArray([...practices, ...club.practices]);
    }, []);
  } else {
    return Array.from(
      new Set(
        clubs.reduce((practices, club) => {
          return [...practices, ...club.practices];
        }, [])
      )
    );
  }
}

export function getCompetitionsFromClubs(clubs: Array<ClubPreviewType>): Array<string> {
  if (!Array.from) {
    /**
     * Cas des vieux IE
     */
    return uniqueArray(
      clubs.reduce((competitions, club) => {
        return [...competitions, ...club.competitions];
      }, [])
    );
  } else {
    return Array.from(
      new Set(
        clubs.reduce((competitions, club) => {
          return [...competitions, ...club.competitions];
        }, [])
      )
    );
  }
}

export function hitToClubPreview(hit: SearchClub): ClubPreviewType {
  return {
    id: hit.id,
    img: 'https://api-agregateur-static.ffr.fr/assets' + hit.embleme,
    name: hit.nom,
    searchName: hit.nom,
    lat: hit._geoloc ? hit._geoloc.lat : 0,
    long: hit._geoloc ? hit._geoloc.lng : 0,
    competitions: [],
    practices: hit.pratiques,
    adress: {
      complement: '',
      street: '',
      postalCode: hit['Adresse.codePostal'],
      locality: hit['Adresse.localite']
    },
    slug: hit.identifiant,
    _geoDistance: hit._geoDistance ? hit._geoDistance : 0
  };
}

export function getLigueSearchInfos(ligue: number) {
  const ligues = {
    '3089': {
      lat: 45.7831,
      lon: 3.0824,
      nom: 'Auvergne-Rhône-Alpes'
    },
    '3091': {
      lat: 48.2232, // loudéac
      lon: -2.7713,
      nom: 'Bretagne'
    },
    '3090': {
      lat: 47.0747, // Beaune
      lon: 4.8399,
      nom: 'Bourgogne-Franche-Comté'
    },
    '3092': {
      lat: 47.6221, // château de chambord
      lon: 1.5346,
      nom: 'Centre-Val de Loire'
    },
    '3093': {
      lat: 42.0901, // montpon Ménestrol
      lon: 9.0105,
      nom: 'Corse' // à checker pas ok sur l'api
    },
    '3094': {
      lat: 48.6819, // toul
      lon: 5.8864,
      nom: 'Grand Est'
    },
    '3095': {
      lat: 50.0015, // albert
      lon: 2.6445,
      nom: 'Hauts-de-France'
    },
    '3096': {
      lat: 48.7537, // créteil
      lon: 2.4438,
      nom: 'Île-de-France'
    },
    '3098': {
      lat: 45.1409, // montpon Ménestrol
      lon: -0.0133,
      nom: 'Nouvelle-Aquitaine'
    },
    '3097': {
      lat: 49.1587, // lisieux
      lon: 0.2479,
      nom: 'Normandie'
    },
    '3099': {
      lat: 43.9447, // albi
      lon: 2.1406,
      nom: 'Occitanie'
    },
    '3101': {
      lat: 47.5012, // angers
      lon: -0.564,
      nom: 'Pays de la Loire'
    },
    '3102': {
      lat: 44.0993, // digne les bains
      lon: 6.2393,
      nom: "Provence-Alpes-Côte d'Azur"
    }
  };

  return ligues[ligue.toString()] ? ligues[ligue.toString()] : ligues['3089'];
}

const API_ADRESSE = 'https://api-adresse.data.gouv.fr';
const API_REQUEST_LIMIT = 50;

async function search(args: any) {
  const url = `${API_ADRESSE}/search/?${qs.stringify(args)}`;
  return await _fetch(url);
}
 
export async function getLeagueCityList(input: string, structureId: number): any {
  const ligueData = getLigueSearchInfos(structureId);

  const results = await search({
    q: input.trim(),
    limit: API_REQUEST_LIMIT,
    type: 'municipality',
    lat: ligueData.lat,
    lon: ligueData.lon
  });
  // var re = new RegExp(ligueData.nom, 'g'); // ! trop de rerender avec la regex... => plante
  const re = ligueData.nom;

  const filteredResults = results.features.filter(feature => feature.properties.context.includes(re));

  return filteredResults;
}
